/**
 * app.js
 * Punto de entrada de la aplicación.
 * Orquesta la carga de datos, renderizado,
 * la conexión de eventos y la autenticación.
 */

import { setRecetas, setBusqueda, setCategoriaActiva, setError } from './state.js';
import { aplicarFiltros } from './filters.js';
import { renderLista, renderError, renderCargando } from './render.js';
import { mostrarDetalle } from './detail.js';
import { registrarServiceWorker } from './sw-register.js';
import { supabase } from './supabase.js';
import {
  login,
  logout,
  onCambioSesion,
  esAdmin,
  registro,
  estaAprobado,
  cambiarPassword,
} from './auth.js';
import { abrirEdicion, abrirCreacion } from './edit.js';
import { guardarRecetasEnCache, leerRecetasDeCache } from './cache.js';

// ── Elementos del DOM ─────────────────────────────────

const pantallaLogin = document.getElementById('pantalla-login');
const formLogin = document.getElementById('form-login');
const inputEmail = document.getElementById('login-email');
const inputPassword = document.getElementById('login-password');
const errorLogin = document.getElementById('login-error');
const btnLogin = document.getElementById('btn-login');
const pantallaRegistro = document.getElementById('pantalla-registro');
const formRegistro = document.getElementById('form-registro');
const inputRegEmail = document.getElementById('registro-email');
const inputRegPassword = document.getElementById('registro-password');
const errorRegistro = document.getElementById('registro-error');
const exitoRegistro = document.getElementById('registro-exito');
const btnRegistro = document.getElementById('btn-registro');
const pantallaPendiente = document.getElementById('pantalla-pendiente');

// ── Mostrar / ocultar pantalla de login ───────────────

function mostrarLogin() {
  pantallaLogin.setAttribute('aria-hidden', 'false');
  inputEmail.focus();
}

function ocultarLogin() {
  pantallaLogin.setAttribute('aria-hidden', 'true');
}

function mostrarRegistro() {
  pantallaLogin.setAttribute('aria-hidden', 'true');
  pantallaRegistro.setAttribute('aria-hidden', 'false');
  inputRegEmail.focus();
}

function ocultarRegistro() {
  pantallaRegistro.setAttribute('aria-hidden', 'true');
}

function mostrarPendiente() {
  pantallaLogin.setAttribute('aria-hidden', 'true');
  pantallaRegistro.setAttribute('aria-hidden', 'true');
  pantallaPendiente.setAttribute('aria-hidden', 'false');
}

function ocultarPendiente() {
  pantallaPendiente.setAttribute('aria-hidden', 'true');
}

// ── Carga de datos ────────────────────────────────────

// Variable para el callback de edición, accesible en todo app.js
let _cbEdicion = null;
let _esAdmin = false;

async function cargarRecetas() {
  try {
    renderCargando();

    let recetas = null;
    let desdeCache = false;

    try {
      // Intentar cargar desde Supabase
      const [{ data, error }, admin] = await Promise.all([
        supabase.from('recetas').select('*').order('nombre', { ascending: true }),
        esAdmin(),
      ]);

      if (error) {
        throw new Error(error.message);
      }

      recetas = data;
      _esAdmin = admin;

      // Guardar en caché para uso offline
      await guardarRecetasEnCache(recetas);
    } catch (errorRed) {
      // Sin red — intentar cargar desde caché
      console.warn('[App] Sin conexión, cargando desde caché...');
      recetas = await leerRecetasDeCache();
      desdeCache = true;

      if (!recetas || recetas.length === 0) {
        throw new Error('Sin conexión y sin caché disponible');
      }
    }

    // Construir callback de edición (solo si no es caché)
    _cbEdicion =
      !desdeCache && _esAdmin
        ? (receta) =>
            abrirEdicion(receta, () => {
              aplicarFiltros();
              renderLista(mostrarDetalle, _esAdmin, _cbEdicion);
            })
        : null;

    setRecetas(recetas);
    aplicarFiltros();
    renderLista(mostrarDetalle, _esAdmin, _cbEdicion);
    actualizarContadorHeader(recetas.length);
    ocultarPantallaInicio();

    // Mostrar aviso si estamos en modo offline
    if (desdeCache) {
      mostrarAvisoOffline();
    }

    // Mostrar botón nueva receta si es admin y hay conexión
    const btnNueva = document.getElementById('btn-nueva-receta');
    if (_esAdmin && !desdeCache) {
      btnNueva.style.display = 'block';
      btnNueva.onclick = () => {
        abrirCreacion(() => {
          aplicarFiltros();
          renderLista(mostrarDetalle, _esAdmin, _cbEdicion);
        });
      };
    }
  } catch (error) {
    setError(error.message);
    renderError(error.message);
    ocultarPantallaInicio();
    console.error('[App] Error al cargar recetas:', error);
  }
}

// ── Aviso modo offline ────────────────────────────────

function mostrarAvisoOffline() {
  const meta = document.querySelector('.header__meta');
  if (meta) {
    meta.textContent = 'Recetario · Modo sin conexión';
    meta.style.color = 'var(--color-accent)';
  }
}

// ── Actualizar header ─────────────────────────────────

function actualizarContadorHeader(total) {
  const meta = document.querySelector('.header__meta');
  if (meta) {
    meta.textContent = `Recetario · ${total} recetas`;
  }
}

// ── Conexión de eventos de filtros ────────────────────

function conectarEventos() {
  const buscador = document.getElementById('buscador');
  buscador.addEventListener('input', (e) => {
    setBusqueda(e.target.value);
    aplicarFiltros();
    renderLista(mostrarDetalle, _esAdmin, _cbEdicion);
  });

  const filtros = document.querySelectorAll('.filtro-btn');
  filtros.forEach((btn) => {
    btn.addEventListener('click', () => {
      filtros.forEach((b) => {
        b.classList.remove('filtro-btn--activo');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('filtro-btn--activo');
      btn.setAttribute('aria-pressed', 'true');
      setCategoriaActiva(btn.dataset.categoria);
      aplicarFiltros();
      renderLista(mostrarDetalle, _esAdmin, _cbEdicion);
    });
  });

  document.addEventListener('click', (e) => {
    if (e.target.id === 'btn-limpiar') {
      buscador.value = '';
      setBusqueda('');
      setCategoriaActiva('todas');
      filtros.forEach((b) => {
        b.classList.remove('filtro-btn--activo');
        b.setAttribute('aria-pressed', 'false');
      });
      filtros[0].classList.add('filtro-btn--activo');
      filtros[0].setAttribute('aria-pressed', 'true');
      aplicarFiltros();
      renderLista(mostrarDetalle, _esAdmin, _cbEdicion);
      buscador.focus();
    }
  });
}

// ── Eventos de login ──────────────────────────────────

function conectarEventosLogin() {
  formLogin.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorLogin.textContent = '';
    btnLogin.disabled = true;
    btnLogin.textContent = 'Entrando...';

    const { error } = await login(inputEmail.value.trim(), inputPassword.value);

    if (error) {
      errorLogin.textContent = 'Correo o contraseña incorrectos';
      btnLogin.disabled = false;
      btnLogin.textContent = 'Entrar';
    }
    // Si login OK → onCambioSesion se dispara automáticamente
  });

  // Botón logout
  document.getElementById('btn-logout').addEventListener('click', async () => {
    await logout();
    // onCambioSesion se dispara automáticamente y muestra el login
  });

  document.getElementById('btn-logout-pendiente').addEventListener('click', async () => {
    await logout();
  });
}

// ── Eventos de registro ──────────────────────────────────

function conectarEventosRegistro() {
  // Ir a registro
  document.getElementById('btn-ir-registro').addEventListener('click', mostrarRegistro);

  // Volver al login
  document.getElementById('btn-volver-login').addEventListener('click', () => {
    ocultarRegistro();
    mostrarLogin();
  });

  // Enviar registro
  formRegistro.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorRegistro.textContent = '';
    exitoRegistro.textContent = '';
    btnRegistro.disabled = true;
    btnRegistro.textContent = 'Creando cuenta...';

    const { error } = await registro(inputRegEmail.value.trim(), inputRegPassword.value);

    if (error) {
      errorRegistro.textContent = error.message;
      btnRegistro.disabled = false;
      btnRegistro.textContent = 'Crear cuenta';
      return;
    }

    // Éxito — mostrar mensaje y volver al login
    exitoRegistro.textContent = '¡Cuenta creada! Revisa tu correo para confirmarla.';
    btnRegistro.disabled = false;
    btnRegistro.textContent = 'Crear cuenta';

    // Limpiar campos
    inputRegEmail.value = '';
    inputRegPassword.value = '';
  });
}

// ── Eventos de cambio de contraseña ──────────────────

function conectarEventosPassword() {
  const pantalla = document.getElementById('pantalla-password');
  const form = document.getElementById('form-password');
  const inputNueva = document.getElementById('password-nueva');
  const inputConfirmar = document.getElementById('password-confirmar');
  const errorEl = document.getElementById('password-error');
  const exitoEl = document.getElementById('password-exito');
  const btn = document.getElementById('btn-password');

  document.getElementById('btn-cambiar-password').addEventListener('click', () => {
    pantalla.setAttribute('aria-hidden', 'false');
    inputNueva.focus();
  });

  document.getElementById('btn-cerrar-password').addEventListener('click', () => {
    pantalla.setAttribute('aria-hidden', 'true');
    errorEl.textContent = '';
    exitoEl.textContent = '';
    inputNueva.value = '';
    inputConfirmar.value = '';
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.textContent = '';
    exitoEl.textContent = '';

    const nueva = inputNueva.value;
    const confirmar = inputConfirmar.value;

    if (nueva.length < 6) {
      errorEl.textContent = 'La contraseña debe tener al menos 6 caracteres.';
      return;
    }

    if (nueva !== confirmar) {
      errorEl.textContent = 'Las contraseñas no coinciden.';
      return;
    }

    btn.disabled = true;
    btn.textContent = 'Guardando...';

    const { error } = await cambiarPassword(nueva);

    if (error) {
      errorEl.textContent = 'Error al cambiar la contraseña. Inténtalo de nuevo.';
    } else {
      exitoEl.textContent = '¡Contraseña actualizada correctamente!';
      inputNueva.value = '';
      inputConfirmar.value = '';
      // Cerrar el panel después de 2 segundos
      setTimeout(() => {
        pantalla.setAttribute('aria-hidden', 'true');
        exitoEl.textContent = '';
      }, 2000);
    }

    btn.disabled = false;
    btn.textContent = 'Guardar contraseña';
  });
}

// ── Pantalla de inicio (splash) ───────────────────────

function ocultarPantallaInicio() {
  const pantalla = document.getElementById('pantalla-inicio');
  if (!pantalla) {
    return;
  }
  pantalla.classList.add('pantalla-inicio--oculta');
  pantalla.addEventListener('transitionend', () => pantalla.remove(), { once: true });
}

// ── Punto de entrada ──────────────────────────────────

async function init() {
  registrarServiceWorker();
  conectarEventos();
  conectarEventosLogin();
  conectarEventosRegistro();
  conectarEventosPassword();

  // Comprobación inicial — espera a que Supabase restaure la sesión
  const { data } = await supabase.auth.getSession();

  if (data.session) {
    const aprobado = await estaAprobado();
    if (aprobado) {
      ocultarLogin();
      ocultarPendiente();
      await cargarRecetas();
    } else {
      mostrarPendiente();
      ocultarPantallaInicio();
    }
  } else {
    mostrarLogin();
    ocultarPantallaInicio();
  }

  // onCambioSesion solo para login y logout posteriores
  onCambioSesion(async (event, session) => {
    if (event === 'SIGNED_IN') {
      const aprobado = await estaAprobado();
      if (aprobado) {
        ocultarLogin();
        ocultarPendiente();
        await cargarRecetas();
      } else {
        mostrarPendiente();
        ocultarPantallaInicio();
      }
    } else if (event === 'SIGNED_OUT') {
      mostrarLogin();
      ocultarPantallaInicio();
    }
  });
}

document.addEventListener('DOMContentLoaded', init);
